/**
 * 智能风格转换服务 - 图生图/风格迁移
 * 优先使用 Seededit（已下架则不可用）；无则回退到已配置的 Seedream 4.5（支持参考图+风格描述）
 * 文档: https://www.volcengine.com/docs/82379/1824691 Seededit | https://www.volcengine.com/docs/82379/1541523 Seedream 4.0-4.5
 */

import { alibabaAI, modelAPI } from './api';
import { getImageModelConfig } from './imageGen';

export interface StyleTransferModelConfig {
  id: string;
  name: string;
  type: string;
  provider: string;
  api_key: string;
  base_url?: string;
  model_name: string;
  is_active: boolean;
}

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

/** Seedream 4.5 参考图生成时若接口不支持 adaptive，使用的默认尺寸（总像素≥3686400） */
const FALLBACK_SIZE = '1920x1920';

/**
 * 获取风格转换（图生图）模型配置：优先 image_edit，否则启用中的 image 模型
 */
export async function getStyleTransferModelConfig(): Promise<StyleTransferModelConfig | null> {
  try {
    const models = await modelAPI.getAllModels();
    
    const imageEditModels = models
      .filter(model => model.is_active && model.type === 'image_edit')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (imageEditModels.length > 0) {
      return imageEditModels[0] as StyleTransferModelConfig;
    }

    const seededitModels = models
      .filter(model => model.is_active && model.type === 'image' && String(model.model_name || '').includes('seededit'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (seededitModels.length > 0) {
      return seededitModels[0] as StyleTransferModelConfig;
    }

    return getImageModelConfig() as Promise<StyleTransferModelConfig | null>;
  } catch (error) {
    console.error('获取风格转换模型配置失败:', error);
    return getImageModelConfig() as Promise<StyleTransferModelConfig | null>;
  }
}

/**
 * 调用图生图 API；阿里走后端图像代理（可带参考图）
 */
export async function generateStyleTransfer(
  config: StyleTransferModelConfig,
  imageUrl: string,
  prompt: string,
  size: string = 'adaptive'
): Promise<string> {
  if (config.provider === 'alibaba') {
    const data = await alibabaAI.image({
      prompt: prompt.trim(),
      imageUrl: imageUrl.trim(),
      model_id: config.id,
    });
    if (!data?.url) throw new Error('接口未返回图片');
    return data.url;
  }

  const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
  const url = `${baseUrl}/images/generations`;

  const isSeedream = /seedream/i.test(config.model_name || '');
  const effectiveSize = isSeedream ? (size === 'adaptive' ? FALLBACK_SIZE : size) : size;

  const body: Record<string, unknown> = {
    model: config.model_name,
    prompt: prompt.trim(),
    image: imageUrl.trim(),
    size: effectiveSize,
    response_format: 'url',
    watermark: true,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.api_key}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: { message?: string }; message?: string };
    const msg = err?.error?.message || err?.message || `请求失败 ${response.status}`;
    throw new Error(msg);
  }

  const data = (await response.json()) as {
    data?: Array<{ url?: string; b64_json?: string }>;
    images?: Array<{ url?: string }>;
  };

  const list = data.data ?? data.images;
  if (Array.isArray(list) && list.length > 0) {
    const first = list[0];
    if (typeof first?.url === 'string') return first.url;
    if (typeof (first as { b64_json?: string }).b64_json === 'string') {
      return `data:image/png;base64,${(first as { b64_json: string }).b64_json}`;
    }
  }
  throw new Error('接口未返回图片');
}

